import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { supportAgentApi, SupportAgentGlobalMode } from './support-agent-api';
import { ChatAutoResolveSection } from './chat-auto-resolve-section';

// Basic client-side shape check for the "add sender" field — a speed bump
// against an obvious typo, not full RFC validation. The server (Zod
// `.email()`, ≤254 chars) is the real gate; this just avoids a round trip
// for the common "forgot the @" case.
const BASIC_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_IGNORED_SENDER_EMAIL_LENGTH = 254;

// User-facing feature name (Carlos, 8/18 spec correction) — "Filtered
// senders", NEVER "ignored"/"spam" in copy. A filtered sender is a distinct
// concept from the sales/spam pre-draft triage: it's an address an admin
// explicitly added here, and the bot stands down for it in every mode, even
// on tickets already enrolled for live posting. The underlying API/type
// names (getIgnoredSenders, IgnoredSendersResult, etc.) stay as-is — they
// mirror the pinned `agentConfig{key:'ignoredSenders'}` contract on the
// server/worker side, which this UI-copy rename does not touch.
const FILTERED_SENDERS_CAPTION =
  "Messages from these senders appear in Activity as \"Filtered · sender\" and the bot stands down in every mode, even on enrolled tickets.";

/**
 * True if `a` and `b` contain the same emails, ignoring order — used for the
 * filtered-senders "dirty" check below. Chip removal/re-add can reorder the
 * working list relative to the last-saved baseline even when the set of
 * emails is unchanged, so a plain array-equality check would show a false
 * "unsaved changes" state.
 */
function sameSenderSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((email, i) => email === sortedB[i]);
}

const MODE_LABEL: Record<SupportAgentGlobalMode, string> = {
  observe: 'Observe',
  shadow: 'Shadow',
  live: 'Live',
};

const MODE_OPTIONS: { value: SupportAgentGlobalMode; description: string }[] = [
  { value: 'observe', description: 'Record tickets only — no AI calls, no drafts.' },
  { value: 'shadow', description: 'Draft on every ticket for review here — nothing posted to Zendesk.' },
  {
    value: 'live',
    description: 'Post every draft to Zendesk as an internal note; urgent tickets get an escalation banner note.',
  },
];

// `mode: null` (no explicit setting has ever been saved) is displayed as
// this — the worker's own deployment default is shadow in practice, so a
// null response is shown pre-selected on Shadow with a caption explaining
// it's not an explicit choice yet.
const DEFAULT_DISPLAY_MODE: SupportAgentGlobalMode = 'shadow';

// Case-sensitive on purpose — this is a deliberate speed bump before the
// mode that posts an AI note on every ticket in the org, not a casual
// confirm. Matches the spec's literal "type LIVE".
const LIVE_CONFIRM_PHRASE = 'LIVE';

/**
 * Settings tab (Carlos, 8/17) — the global support-agent drafting mode. Same
 * singleton-store caveat as every other panel in this tab (see index.tsx's
 * header comment): there is one live setting, shared by dev, QA, and prod.
 * Per-ticket posting enrollment (activity-panel.tsx's Switch) is
 * independent of this — an enrolled ticket keeps posting regardless of
 * whatever this panel is set to.
 */
export const SettingsPanel: React.FC = () => {
  const [currentMode, setCurrentMode] = useState<SupportAgentGlobalMode | null>(null);
  const [updatedBy, setUpdatedBy] = useState<string | undefined>(undefined);
  const [updatedAt, setUpdatedAt] = useState<string | undefined>(undefined);
  const [selectedMode, setSelectedMode] = useState<SupportAgentGlobalMode>(DEFAULT_DISPLAY_MODE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [liveConfirmText, setLiveConfirmText] = useState('');

  // ── Ignored senders (Carlos, 8/18) ─────────────────────────────────────
  // `savedSenders` is the last-known-persisted baseline (used for the dirty
  // check and as the revert target on a failed save); `localSenders` is the
  // working draft the chips/add-field render from.
  const [savedSenders, setSavedSenders] = useState<string[]>([]);
  const [localSenders, setLocalSenders] = useState<string[]>([]);
  const [ignoredUpdatedBy, setIgnoredUpdatedBy] = useState<string | undefined>(undefined);
  const [ignoredUpdatedAt, setIgnoredUpdatedAt] = useState<string | undefined>(undefined);
  const [ignoredLoading, setIgnoredLoading] = useState(true);
  const [ignoredError, setIgnoredError] = useState<string | null>(null);
  const [ignoredSaving, setIgnoredSaving] = useState(false);
  const [newSender, setNewSender] = useState('');
  const [addSenderError, setAddSenderError] = useState<string | null>(null);

  const loadIgnoredSenders = async () => {
    setIgnoredLoading(true);
    setIgnoredError(null);
    try {
      const res = await supportAgentApi.getIgnoredSenders();
      setSavedSenders(res.senders);
      setLocalSenders(res.senders);
      setIgnoredUpdatedBy(res.updatedBy);
      setIgnoredUpdatedAt(res.updatedAt);
    } catch (err: any) {
      setIgnoredError(err?.response?.data?.message || err.message);
    } finally {
      setIgnoredLoading(false);
    }
  };

  const ignoredSendersDirty = !sameSenderSet(localSenders, savedSenders);

  const handleAddSender = () => {
    const email = newSender.trim().toLowerCase();
    if (!email) return;
    if (email.length > MAX_IGNORED_SENDER_EMAIL_LENGTH) {
      setAddSenderError(`Email is too long (max ${MAX_IGNORED_SENDER_EMAIL_LENGTH} characters)`);
      return;
    }
    if (!BASIC_EMAIL_REGEX.test(email)) {
      setAddSenderError('Enter a valid email address');
      return;
    }
    if (localSenders.includes(email)) {
      setAddSenderError('Already in the list');
      return;
    }
    setLocalSenders((prev) => [...prev, email]);
    setNewSender('');
    setAddSenderError(null);
  };

  const handleRemoveSender = (email: string) => {
    setLocalSenders((prev) => prev.filter((s) => s !== email));
  };

  const handleDiscardSenderChanges = () => {
    setLocalSenders(savedSenders);
    setNewSender('');
    setAddSenderError(null);
    setIgnoredError(null);
  };

  /**
   * Confirm-free optimistic save: the baseline (`savedSenders`) flips to the
   * new list immediately — clearing the dirty state and disabling Save
   * right away, before the request resolves — rather than waiting on the
   * round trip. On failure both the baseline AND the working draft revert
   * to what they were before the click, and the error is shown inline.
   */
  const handleSaveIgnoredSenders = async () => {
    const previousSaved = savedSenders;
    const previousUpdatedBy = ignoredUpdatedBy;
    const previousUpdatedAt = ignoredUpdatedAt;
    const toSave = localSenders;

    setIgnoredSaving(true);
    setIgnoredError(null);
    setSavedSenders(toSave);

    try {
      const res = await supportAgentApi.updateIgnoredSenders(toSave);
      setSavedSenders(res.senders);
      setLocalSenders(res.senders);
      setIgnoredUpdatedBy(res.updatedBy);
      setIgnoredUpdatedAt(res.updatedAt);
    } catch (err: any) {
      setSavedSenders(previousSaved);
      setLocalSenders(previousSaved);
      setIgnoredUpdatedBy(previousUpdatedBy);
      setIgnoredUpdatedAt(previousUpdatedAt);
      setIgnoredError(err?.response?.data?.message || err.message);
    } finally {
      setIgnoredSaving(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await supportAgentApi.getAgentMode();
      setCurrentMode(res.mode);
      setUpdatedBy(res.updatedBy);
      setUpdatedAt(res.updatedAt);
      setSelectedMode(res.mode ?? DEFAULT_DISPLAY_MODE);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadIgnoredSenders();
  }, []);

  // What the radio group shows as "currently selected" before any local
  // edit — a null server value displays as Shadow (see DEFAULT_DISPLAY_MODE
  // above), so picking Shadow explicitly while the server is still null is
  // NOT treated as dirty (nothing would actually change).
  const effectiveCurrent = currentMode ?? DEFAULT_DISPLAY_MODE;
  const isDirty = selectedMode !== effectiveCurrent;

  const openConfirm = () => {
    setLiveConfirmText('');
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (saving) return;
    setConfirmOpen(false);
  };

  const confirmChange = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await supportAgentApi.updateAgentMode(selectedMode);
      setCurrentMode(res.mode);
      setUpdatedBy(res.updatedBy);
      setUpdatedAt(res.updatedAt);
      setNotice(`Mode changed to ${MODE_LABEL[res.mode ?? selectedMode]}.`);
      setConfirmOpen(false);
    } catch (err: any) {
      // Dialog stays open on failure — the error is shown inside it (see
      // below) so the user can see what happened without losing their
      // selection or having to reopen the confirm flow.
      setError(err?.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const isLiveSelection = selectedMode === 'live';
  const liveGateSatisfied = liveConfirmText === LIVE_CONFIRM_PHRASE;
  const confirmDisabled = saving || (isLiveSelection && !liveGateSatisfied);

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      {error && !confirmOpen && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Agent mode
      </Typography>

      {currentMode === null && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Using deployment default
        </Typography>
      )}
      {updatedBy && updatedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Last changed by {updatedBy} at {new Date(updatedAt).toLocaleString()}
        </Typography>
      )}

      <FormControl component="fieldset" sx={{ mb: 1 }}>
        <RadioGroup
          aria-label="Agent mode"
          name="support-agent-mode"
          value={selectedMode}
          onChange={(e) => setSelectedMode(e.target.value as SupportAgentGlobalMode)}
        >
          {MODE_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={<Radio />}
              label={
                <Box sx={{ py: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {MODE_LABEL[opt.value]}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {opt.description}
                  </Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>
      </FormControl>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Individual tickets enrolled for posting stay live regardless of this setting.
      </Typography>

      <Stack direction="row" spacing={2}>
        <Button variant="contained" disabled={!isDirty || saving} onClick={openConfirm}>
          Save
        </Button>
        {isDirty && (
          <Button variant="text" disabled={saving} onClick={() => setSelectedMode(effectiveCurrent)}>
            Discard changes
          </Button>
        )}
      </Stack>

      <Dialog open={confirmOpen} onClose={closeConfirm}>
        <DialogTitle>
          {isLiveSelection ? 'Switch the support agent to Live mode?' : `Change mode to ${MODE_LABEL[selectedMode]}?`}
        </DialogTitle>
        <DialogContent>
          {isLiveSelection ? (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                Agents will start seeing AI notes on ALL tickets — every draft posts to Zendesk as an internal
                note, and urgent tickets get an escalation banner note. This is a single setting shared by dev,
                QA, and prod.
              </DialogContentText>
              <DialogContentText sx={{ mb: 1 }}>
                Type <strong>{LIVE_CONFIRM_PHRASE}</strong> to confirm.
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                label={`Type ${LIVE_CONFIRM_PHRASE} to confirm`}
                value={liveConfirmText}
                onChange={(e) => setLiveConfirmText(e.target.value)}
                disabled={saving}
              />
            </>
          ) : (
            <DialogContentText>
              This changes the live drafting mode for every ticket the bot handles, except tickets individually
              enrolled for posting.
            </DialogContentText>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" disabled={confirmDisabled} onClick={confirmChange}>
            {saving ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Divider sx={{ my: 3 }} />

      <ChatAutoResolveSection />

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Filtered senders
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {FILTERED_SENDERS_CAPTION}
      </Typography>
      {ignoredUpdatedBy && ignoredUpdatedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Last changed by {ignoredUpdatedBy} at {new Date(ignoredUpdatedAt).toLocaleString()}
        </Typography>
      )}

      {ignoredError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {ignoredError}
        </Alert>
      )}

      {ignoredLoading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, minHeight: 32 }}>
            {localSenders.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No filtered senders configured.
              </Typography>
            ) : (
              localSenders.map((email) => (
                <Chip key={email} size="small" label={email} onDelete={() => handleRemoveSender(email)} />
              ))
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="flex-start">
            <TextField
              size="small"
              label="Add sender email"
              placeholder="name@example.com"
              value={newSender}
              onChange={(e) => {
                setNewSender(e.target.value);
                setAddSenderError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddSender();
                }
              }}
              error={!!addSenderError}
              helperText={addSenderError}
              disabled={ignoredSaving}
            />
            <Button variant="outlined" onClick={handleAddSender} disabled={ignoredSaving || !newSender.trim()}>
              Add
            </Button>
          </Stack>

          <Stack direction="row" spacing={2}>
            {/* aria-label distinguishes this from the Agent mode section's own
                "Save" button above — same page, same visible label by design,
                but tests/assistive tech need to tell them apart. */}
            <Button
              variant="contained"
              aria-label="Save filtered senders"
              disabled={!ignoredSendersDirty || ignoredSaving}
              onClick={handleSaveIgnoredSenders}
            >
              {ignoredSaving ? 'Saving…' : 'Save'}
            </Button>
            {ignoredSendersDirty && (
              <Button
                variant="text"
                aria-label="Discard filtered sender changes"
                disabled={ignoredSaving}
                onClick={handleDiscardSenderChanges}
              >
                Discard changes
              </Button>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default SettingsPanel;
